import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import {
  Blueprint,
  BlueprintDeployment,
  BlueprintInput,
  BlueprintInputType,
} from "types/generated";

import { BLUEPRINT_CREATE_STACK, BLUEPRINT_UPDATE_DEPLOYMENT } from "./gql";
import { VersionTemplateFormFields } from "./types";

type UseDeploymentFormOptions = {
  version: Blueprint;
  deployment?: BlueprintDeployment;
  templateId?: string;
  submitCallback?: (deploymentId?: string) => void;
};

const useDeploymentForm = ({
  version,
  deployment,
  templateId,
  submitCallback,
}: UseDeploymentFormOptions) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [inputs, setInputs] = useState<BlueprintInput[]>(version.inputs);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const hasErrors = errors.length > 0;
  const hasPreview = inputs.length > 0;
  const isUpdateMode = !!deployment;

  const [createStack, { loading: stackCreateLoading }] = useMutation(BLUEPRINT_CREATE_STACK);
  const [updateDeployment, { loading: deploymentUpdateLoading }] = useMutation(
    BLUEPRINT_UPDATE_DEPLOYMENT
  );

  const loading = stackCreateLoading || deploymentUpdateLoading;

  const defaultValues = useMemo(() => {
    const inputDefaults = inputs.reduce(
      (acc, input) => {
        const deploymentInput = isUpdateMode && deployment && deployment.inputs.find((di) => di.id === input.id);
        const value = deploymentInput ? deploymentInput.value : input.default;

        if (input.type === BlueprintInputType.Boolean) {
          acc[input.id] = value === "true";
        } else {
          acc[input.id] = value;
        }
        return acc;
      },
      {} as VersionTemplateFormFields["inputs"]
    );

    return {
      name: deployment?.name || "",
      description: deployment?.description || "",
      space: version.space.id,
      inputs: inputDefaults,
      versionId: deployment?.blueprintVersion.id || version.id,
    };
  }, [inputs, version, deployment, isUpdateMode]);

  const builderForm = useForm<VersionTemplateFormFields>({
    defaultValues,
    mode: "onChange",
  });

  const {
    reset,
    formState: { isDirty },
  } = builderForm;

  const onSubmit: SubmitHandler<VersionTemplateFormFields> = (formData) => {
    const inputsPayload = Object.entries(formData.inputs).map(([key, value]) => ({
      id: key,
      value: value?.toString() as string,
      sensitive: false,
    }));

    if (isUpdateMode && deployment) {
      // Only include versionId if it's different from the current deployment version
      const hasVersionChanged =
        formData.versionId && formData.versionId !== deployment.blueprintVersion.id;

      updateDeployment({
        variables: {
          blueprintID: templateId!,
          id: deployment.id,
          input: {
            name: formData.name,
            description: formData.description,
            inputs: inputsPayload,
            versionId: hasVersionChanged ? formData.versionId! : null,
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintDeploymentUpdate) {
            if (submitCallback) {
              submitCallback(data.blueprintDeploymentUpdate.id);
            }
            reportSuccess({ message: `Deployment was successfully updated` });
          }
        })
        .catch(onError);
    } else {
      createStack({
        variables: {
          id: version.id,
          input: {
            name: formData.name,
            space: formData.space,
            description: formData.description,
            inputs: inputsPayload,
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintDeploymentCreate) {
            if (submitCallback) {
              submitCallback(data.blueprintDeploymentCreate?.id);
            }
            reportSuccess({ message: `Deployment was successfully created` });
          }
        })
        .catch(onError);
    }
  };

  useEffect(() => {
    // update default values when inputs change but only if the form is not dirty
    if (!isDirty) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, isDirty]);

  return {
    builderForm,
    onSubmit,
    stackCreateLoading,
    loading,
    isUpdateMode,
    hasErrors,
    hasPreview,
    errors,
    setErrors,
    inputs,
    setInputs,
  };
};

export default useDeploymentForm;
